import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toVideoEmbed } from '../../utils';
import { WbVideoBox, WbVideoCaption, WbVideoWrapper } from './styles';

const animationMs = 760;
const capitionInitial =
  'Hör dir Franz auf Wienerisch an\nKlicke auf Franz, um ihn aufzuwecken';
const captionArmed = 'Noch einmal…\nSchüttle Franz noch einmal!';

interface Props {
  videoUrl: string;
}

const VideoBox: React.FC<Props> = ({ videoUrl }) => {
  const embed = useMemo(() => toVideoEmbed(videoUrl), [videoUrl]);
  const isInteractive = embed.kind === 'file';

  const videoRef = useRef<HTMLVideoElement>(null);
  const typewriterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clickTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [state, setState] = useState<0 | 1 | 2>(0);
  const [canClick, setCanClick] = useState(true);
  const [shake, setShake] = useState<'left' | 'right' | null>(null);
  const [caption, setCaption] = useState('');

  const writeText = (text: string, speed = 35): void => {
    if (typewriterTimeout.current) clearTimeout(typewriterTimeout.current);
    setCaption('');
    let index = 0;
    const next = (): void => {
      index += 1;
      setCaption(text.slice(0, index));
      if (index < text.length) {
        typewriterTimeout.current = setTimeout(next, speed);
      }
    };
    next();
  };

  useEffect(() => {
    if (!isInteractive) return undefined;
    setState(0);
    setCanClick(true);
    setShake(null);
    writeText(capitionInitial);
    return () => {
      if (typewriterTimeout.current) clearTimeout(typewriterTimeout.current);
      if (clickTimeout.current) clearTimeout(clickTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embed.src, isInteractive]);

  const handleClick = (): void => {
    if (!isInteractive || !canClick) return;

    if (state === 0) {
      setCanClick(false);
      setShake('left');
      clickTimeout.current = setTimeout(() => {
        writeText(captionArmed);
        setState(1);
        setCanClick(true);
      }, animationMs);
    } else if (state === 1) {
      setCanClick(false);
      setShake('right');
      clickTimeout.current = setTimeout(() => {
        writeText('');
        setState(2);
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(() => {});
        }
      }, animationMs);
    }
  };

  const handleEnded = (): void => {
    setState(0);
    setCanClick(true);
    if (videoRef.current) videoRef.current.currentTime = 0;
    writeText(capitionInitial);
  };

  if (!isInteractive) {
    return (
      <WbVideoWrapper>
        <WbVideoBox $shake={null}>
          <iframe
            src={embed.src}
            title="video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            frameBorder={0}
          />
        </WbVideoBox>
      </WbVideoWrapper>
    );
  }

  return (
    <WbVideoWrapper>
      <WbVideoBox
        $shake={shake}
        $interactive
        onClick={handleClick}
        onAnimationEnd={() => setShake(null)}
      >
        <video
          ref={videoRef}
          src={embed.src}
          playsInline
          preload="auto"
          onEnded={handleEnded}
        />
      </WbVideoBox>
      <WbVideoCaption>{caption}</WbVideoCaption>
    </WbVideoWrapper>
  );
};

export default VideoBox;
